import React, { useCallback, useState } from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactModal, {
  modalHeightAutoStyles,
  modelExtraRadiusStyles,
} from 'shared/design-system/components/TrueImpactModal';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import styled from 'styled-components';
import ReportSharingOptions, {
  getOrganizationVisibility,
  getOrganizationVisibilityToSharing,
  getReportSharingFromReportVisibility,
  SharingType,
} from './ReportSharingOptions';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import TrueImpactCheckbox from 'shared/design-system/components/TrueImpactCheckbox';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { updateOrganizationVisibility } from './api';
import { useHistory } from 'react-router-dom';

const StyledTerms = styled.div`
  a {
    font-weight: bold;
    text-decoration: none;
    color: ${COLORS.ctaPrimaryHover};
  }
`;

export const Terms = () => (
  <StyledTerms>
    I agree to the{' '}
    <a href="https://www.trueimpact.com/terms-of-use" target="_blank">
      Terms of Service
    </a>{' '}
    and acknowledge the{' '}
    <a href="https://www.trueimpact.com/privacy-policy" target="_blank">
      Privacy Policy
    </a>
    .
  </StyledTerms>
);

const SupportText: React.FC<{ className?: string }> = ({ className }) => (
  <StyledTerms className={className}>
    <a
      target="_blank"
      href="https://support.trueimpact.com/manage-your-report-visibility"
    >
      Learn more
    </a>{' '}
    about this feature and get answers to frequently asked questions.
  </StyledTerms>
);

export const ReportVisibilityText: React.FC<{ className?: string }> = ({
  className,
}) => {
  return (
    <TrueImpactText className={className}>
      <strong>
        Share your impact more widely—and unlock new funding opportunities
      </strong>
      <br />
      <br />
      True Impact lets you make your impact reports visible to{' '}
      <strong>any donor</strong>—across our network of trusted platforms like
      Charity Navigator and Blackbaud, or through a shareable public link and
      downloadable PDF. This increases your discoverability, reduces repeat
      reporting, and helps more funders understand and support your work.
      <br />
      <SupportText className="support-text" />
      Select your report visibility status below:
    </TrueImpactText>
  );
};

const StyledDiv = styled.div`
  padding: ${SPACERS.extraLarge};
  .report-visibility-text {
    margin-bottom: ${SPACERS.tiny};
    .support-text {
      font-size: 14px;
      margin-top: ${SPACERS.tiny};
      margin-bottom: ${SPACERS.small};
    }
  }
  .report-sharing-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${SPACERS.medium};
    margin-top: ${SPACERS.large};
  }
  .blue-text {
    color: ${COLORS.greyDarkest};
  }
  .terms-checkmark {
    margin-bottom: ${SPACERS.medium};
  }
  .confirmation-text {
    font-weight: bold;
    text-align: center;
    color: ${COLORS.critical};
    margin-top: ${SPACERS.medium};
  }
`;
const addStyles = `
  .modal-content {
    border-radius: ${SPACERS.extraTiny};
    background-color: ${COLORS.whiteBase} !important;
  }
`;

const sessionKey = 'reportSharingModalShown';
const getInitialValue = () => {
  const savedValue = sessionStorage.getItem(sessionKey);
  return savedValue ? JSON.parse(savedValue) : true;
};

const ReportSharingModal: React.FC = () => {
  const client = useClient();
  const { push } = useHistory();
  const { isGhosting } = useUserActions();
  const { visibility, newProgramVisibility } = useUserActions();
  const [requestState, makeRequest] = useGraphQLRequest();
  const [terms, setTerms] = useState<boolean>(false);
  const showVisibilityModal =
    visibility === 'UNDECIDED' || visibility === 'SKIPPED';
  const [open, setOpen] = useState<boolean>(getInitialValue());
  const [selection, setSelection] = useState<SharingType>(
    getOrganizationVisibilityToSharing(visibility),
  );
  const [subSelection, setSubSelection] = useState<SharingType>(
    getReportSharingFromReportVisibility(newProgramVisibility),
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    sessionStorage.setItem(sessionKey, 'false');
  }, [setOpen]);

  const handleSubmit = useCallback(() => {
    if (!client || !selection) return;
    const visibility = getOrganizationVisibility(selection) || 'SKIPPED';
    const request = updateOrganizationVisibility(client, {
      visibility,
      newProgramVisibility: subSelection,
      terms: true,
    });
    makeRequest(request);
    request.then(r => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((r.data as any)?.updateAccountVisibility?.success) {
        setOpen(false);
        if (selection === 'custom') {
          push('/account/report-sharing');
        }
      }
    });
  }, [client, selection, subSelection, setOpen]);

  const isLoading = requestState.type === 'REQUEST_START';

  if (!showVisibilityModal || isGhosting) return null;

  return (
    <TrueImpactModal
      open={open}
      hideFooter
      hideHeader
      onClose={handleClose}
      maxWidth="1100px"
      addStyles={modalHeightAutoStyles + modelExtraRadiusStyles + addStyles}
    >
      <StyledDiv>
        <TrueImpactText variant="heading-page">
          One Report, Any Donor
        </TrueImpactText>
        <ReportVisibilityText className="report-visibility-text" />
        <TrueImpactCheckbox
          checked={terms}
          handleCheck={setTerms}
          text={<Terms />}
          className="terms-checkmark"
        />
        <ReportSharingOptions
          disabled={!terms}
          shouldSave={false}
          isLoginModal={true}
          selection={selection}
          subSelection={subSelection}
          setSelection={setSelection}
          setSubSelection={setSubSelection}
          handleClose={handleClose}
        />
        {selection === 'private' && (
          <TrueImpactText className="confirmation-text">
            Are you sure? By choosing this option, all of your reports won’t be
            shared beyond your current donor. That means fewer opportunities for
            additional donors to discover your impact.
          </TrueImpactText>
        )}
        <div className="report-sharing-buttons">
          <TrueImpactButton
            disabled={!terms}
            variant="secondary-large"
            onClick={handleClose}
          >
            Skip for now
          </TrueImpactButton>
          <TrueImpactButton
            isLoading={isLoading}
            variant="primary-large"
            onClick={handleSubmit}
            disabled={!selection || !terms}
          >
            Save &amp; Continue
          </TrueImpactButton>
        </div>
      </StyledDiv>
    </TrueImpactModal>
  );
};

export default ReportSharingModal;
